import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  /** Оператор платформы (доступ к /admin). Отсутствует/false у обычных пользователей. */
  isAdmin?: boolean;
}

interface Tokens {
  accessToken: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  /**
   * Поколение сессии вкладки: растёт при выходе и при входе другим аккаунтом. Ответ refresh,
   * колбэк мутации и внутренний буфер копирования, начатые в прошлом поколении, к новому не
   * применяются — иначе опоздавший результат возвращал бы личность и данные прошлого аккаунта.
   * В sessionStorage не входит: после перезагрузки опоздавших ответов нет.
   */
  sessionEpoch: number;

  /** Устанавливает пользователя и access JWT; refresh — в httpOnly cookie (API). */
  setAuth: (user: User, tokens: Tokens) => void;

  /** Обновляет access JWT (после refresh); cookie обновляет сервер. */
  updateTokens: (tokens: Tokens) => void;

  /** Сбрасывает всё состояние авторизации */
  logout: () => void;
}

type PersistedAuthSlice = Pick<AuthState, 'user' | 'accessToken' | 'isAuthenticated'>;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      sessionEpoch: 0,

      setAuth: (user, tokens) =>
        set((state) => ({
          user,
          accessToken: tokens.accessToken,
          isAuthenticated: true,
          // Тот же аккаунт (refresh, смена аватара) — та же сессия.
          sessionEpoch: state.user?.id === user.id ? state.sessionEpoch : state.sessionEpoch + 1,
        })),

      updateTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
        }),

      logout: () =>
        set((state) => ({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          sessionEpoch:
            state.user !== null || state.accessToken !== null ? state.sessionEpoch + 1 : state.sessionEpoch,
        })),
    }),
    {
      name: 'sloy-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // sessionStorage: access не переживает закрытие вкладки; refresh — httpOnly cookie.
      storage: {
        getItem: (key) => {
          const value = sessionStorage.getItem(key);
          if (!value) return null;
          try {
            const parsed = JSON.parse(value) as { state?: PersistedAuthSlice & { refreshToken?: unknown }; version?: number };
            if (!parsed || typeof parsed !== 'object' || parsed.state === undefined) {
              return null;
            }
            const state = { ...parsed.state };
            if ('refreshToken' in state) {
              delete state.refreshToken;
            }
            return { state, version: parsed.version ?? 0 };
          } catch {
            return null;
          }
        },
        setItem: (key, value) => sessionStorage.setItem(key, JSON.stringify(value)),
        removeItem: (key) => sessionStorage.removeItem(key),
      },
    },
  ),
);
